/**
 * Trust and fit knobs for estimating the sensor→bike mount rotation from a
 * session's accelerometer + GPS streams.
 *
 * The fusion pipeline assumes an x-forward / y-right / z-down mount that reads
 * (0,0,−1)g at rest. Real hardware does not honour that: the RaceBox sits
 * z-up (+1g at rest) and the field unit is tilted ~33° nose-down. This module
 * recovers the rotation from data; these constants gate when the estimate is
 * trustworthy. Any gate failure returns an identity rotation with
 * trusted=false — never a guess. Tuning any value here: edit → `npm run build`
 * in src/PitBrain.Shared → restart/rebuild the clients.
 */

/** Unit: samples. Minimum accelerometer samples to attempt calibration. Default: 100 */
export const SENSOR_CALIBRATION_MIN_SAMPLES = 100

/** Unit: g. Mean specific-force magnitude below this is not gravity-dominated — untrusted. Default: 0.5 */
export const SENSOR_CALIBRATION_MIN_MEAN_G = 0.5

/** Unit: g. Mean specific-force magnitude above this indicates corrupt data — untrusted. Default: 2.0 */
export const SENSOR_CALIBRATION_MAX_MEAN_G = 2.0

/** Unit: m/s². GPS-trend magnitude for a sample to inform the forward-axis (yaw) fit. Default: 0.5 */
export const SENSOR_CALIBRATION_MIN_DYNAMIC_MS2 = 0.5

/** Unit: samples. Minimum qualifying dynamic samples for the yaw fit. Default: 50 */
export const SENSOR_CALIBRATION_MIN_DYNAMIC_SAMPLES = 50

/** Unit: ratio. |Σ a·h| / Σ(|a|·|h|) below this means horizontal accel doesn't line up with one axis — yaw left at 0. Default: 0.3 */
export const SENSOR_CALIBRATION_MIN_FORWARD_FIT = 0.3

/** Unit: deg. Samples whose surrounding median |GPS coordinated lean| reaches this do not inform the roll of the mount. Default: 10 */
export const SENSOR_CALIBRATION_UPRIGHT_MAX_LEAN_DEG = 10

/** Unit: ms. Half-window of the GPS-lean median that gates the roll samples. Default: 500 */
export const SENSOR_CALIBRATION_UPRIGHT_MEDIAN_HALF_MS = 500

/** Unit: ratio. Roll-rate vs GPS-lean-rate correlation at the refined yaw below this leaves the yaw untouched. Default: 0.5 */
export const SENSOR_CALIBRATION_YAW_REFINE_MIN_CORRELATION = 0.5

/** Unit: s. Least riding above the speed floor before refining. Default: 30 */
export const SENSOR_CALIBRATION_YAW_REFINE_MIN_RIDING_S = 30

/** Unit: km/h. Points below this speed do not inform the refinement. Default: 40 */
export const SENSOR_CALIBRATION_YAW_REFINE_MIN_SPEED_KMH = 40

/** Unit: s. Gaussian smoothing applied to the GPS lean and the gyro rates before comparing them. Default: 1.0 */
export const SENSOR_CALIBRATION_YAW_REFINE_SMOOTHING_S = 1.0
